/**
 * components/autotrader/PnLDashboard.tsx
 * P&L dashboard for the Auto-Trader tab: period/source filters, summary
 * stat tiles, a cumulative equity-curve chart, and a per-day P&L bar
 * chart. Fetches independently of the tab's status/log polling in
 * AutoTraderPanel.tsx, so switching period/source never disturbs anything
 * else on the page. Defaults to source='all' (manual + auto together) —
 * this answers "how is the account actually doing," not just the
 * algorithm in isolation; Auto/Manual isolate the automated strategy on
 * its own when that's the question.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import clsx from 'clsx';
import { autotraderApi } from '../../lib/api';
import type { PnlResponse, PnlPeriod, PnlSource, PnlSeriesPoint } from '../../types';

// Pulled from tailwind.config.js — recharts needs real color values, not
// Tailwind class names, since it paints via inline SVG attributes.
const BULL   = '#2ECC71';
const BEAR   = '#E74C3C';
const INK_3  = '#666660';
const BORDER = '#2A2B2C';

const PERIODS: { id: PnlPeriod; label: string }[] = [
  { id: 'day',   label: 'Day'   },
  { id: 'week',  label: 'Week'  },
  { id: 'month', label: 'Month' },
  { id: 'year',  label: 'Year'  },
  { id: 'all',   label: 'All'   },
];

const SOURCES: { id: PnlSource; label: string }[] = [
  { id: 'all',    label: 'All'    },
  { id: 'auto',   label: 'Auto'   },
  { id: 'manual', label: 'Manual' },
];

/** Matches the existing "$" + signed-number convention already used
 * elsewhere in this app (TopBar.tsx's today-P&L, AnalysisPanel.tsx's
 * trade plan) rather than inventing a new negative-sign placement. */
function fmtMoney(v: number): string {
  const sign = v > 0 ? '+' : '';
  return `${sign}$${Math.round(v).toLocaleString('en-US')}`;
}

/** Bare "YYYY-MM-DD" parsed as browser-local midnight, not UTC — avoids
 * the classic off-by-one-day bug in any timezone behind UTC. */
function fmtDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StatTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="stat-box">
      <div className="stat-label">{label}</div>
      <div className={clsx('stat-value', color)}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

interface ChartTooltipProps {
  active?:  boolean;
  payload?: Array<{ payload: PnlSeriesPoint }>;
}

function EquityTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-bg-2 border border-border rounded-md px-2.5 py-1.5 text-2xs font-mono shadow-lg">
      <div className="text-ink-3 mb-0.5">{fmtDate(p.date)}</div>
      <div className="text-ink-1 font-medium">{fmtMoney(p.cumulativePnlDollars)} total</div>
      <div className={p.pnlDollars >= 0 ? 'text-bull' : 'text-bear'}>{fmtMoney(p.pnlDollars)} that day</div>
    </div>
  );
}

function DailyBarTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-bg-2 border border-border rounded-md px-2.5 py-1.5 text-2xs font-mono shadow-lg">
      <div className="text-ink-3 mb-0.5">{fmtDate(p.date)}</div>
      <div className={clsx('font-medium', p.pnlDollars >= 0 ? 'text-bull' : 'text-bear')}>{fmtMoney(p.pnlDollars)}</div>
      <div className="text-ink-3">{p.trades} trade{p.trades !== 1 ? 's' : ''} · {p.wins}W / {p.losses}L</div>
    </div>
  );
}

export function PnLDashboard() {
  const [period, setPeriod]   = useState<PnlPeriod>('all');
  const [source, setSource]   = useState<PnlSource>('all');
  const [data, setData]       = useState<PnlResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const hasLoadedOnce         = useRef(false);

  const fetchPnl = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await autotraderApi.pnl(period, source);
      setData(result); // only replace on success — the previous chart stays visible while refetching, no flash/skeleton
      hasLoadedOnce.current = true;
    } catch {
      setError('Failed to load P&L data');
    } finally {
      setLoading(false);
    }
  }, [period, source]);

  useEffect(() => { fetchPnl(); }, [fetchPnl]);

  const stats  = data?.stats;
  const series = data?.series ?? [];
  const streak = stats?.currentStreak ?? 0;

  return (
    <div className="card space-y-4">
      {/* Filters — one row, above the charts, scoping everything below */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="section-label mb-0">P&amp;L</div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1">
            {PERIODS.map(p => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={clsx('text-2xs font-mono px-2 py-0.5 rounded border transition-colors',
                  period === p.id ? 'bg-bull/15 border-bull/40 text-bull' : 'border-border text-ink-3 hover:text-ink-1 hover:border-border-strong')}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {SOURCES.map(s => (
              <button key={s.id} onClick={() => setSource(s.id)}
                className={clsx('text-2xs font-mono px-2 py-0.5 rounded border transition-colors',
                  source === s.id ? 'bg-sky/15 border-sky/40 text-sky' : 'border-border text-ink-3 hover:text-ink-1 hover:border-border-strong')}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <div className="text-2xs text-bear">{error}</div>}

      {!hasLoadedOnce.current && loading ? (
        <div className="text-2xs text-ink-3 py-6 text-center">Loading P&amp;L…</div>
      ) : (
        <div className={clsx('space-y-4 transition-opacity', loading && 'opacity-60')}>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile
              label="Total P&L"
              value={fmtMoney(stats?.totalPnlDollars ?? 0)}
              sub={`${(stats?.totalPnlPct ?? 0) >= 0 ? '+' : ''}${(stats?.totalPnlPct ?? 0).toFixed(1)}%`}
              color={(stats?.totalPnlDollars ?? 0) >= 0 ? 'text-bull' : 'text-bear'}
            />
            <StatTile
              label="Win Rate"
              value={`${stats?.winRate ?? 0}%`}
              sub={`${stats?.totalTrades ?? 0} trade${(stats?.totalTrades ?? 0) !== 1 ? 's' : ''}`}
            />
            <StatTile
              label="Profit Factor"
              value={stats?.profitFactor != null ? stats.profitFactor.toFixed(2) : '—'}
              sub={stats?.profitFactor == null ? 'no losses yet' : undefined}
            />
            <StatTile
              label="Current Streak"
              value={streak === 0 ? '—' : `${Math.abs(streak)} ${streak > 0 ? 'win' : 'loss'}${Math.abs(streak) !== 1 ? 's' : ''}`}
              color={streak > 0 ? 'text-bull' : streak < 0 ? 'text-bear' : undefined}
            />
            <StatTile label="Avg Win"  value={fmtMoney(stats?.avgWin ?? 0)} color="text-bull" />
            <StatTile label="Avg Loss" value={`-$${(stats?.avgLoss ?? 0).toFixed(0)}`} color="text-bear" />
            <StatTile
              label="Best Trade"
              value={stats?.bestTrade ? fmtMoney(stats.bestTrade.pnlDollars) : '—'}
              sub={stats?.bestTrade?.sym}
              color="text-bull"
            />
            <StatTile
              label="Worst Trade"
              value={stats?.worstTrade ? fmtMoney(stats.worstTrade.pnlDollars) : '—'}
              sub={stats?.worstTrade?.sym}
              color="text-bear"
            />
          </div>

          {series.length === 0 ? (
            <div className="text-2xs text-ink-3 text-center py-8">No closed trades in this period yet</div>
          ) : (
            <>
              {/* Equity curve — single series, one hue throughout regardless
                  of sign (cumulative total is a running number, not a
                  per-point win/loss) */}
              <div>
                <div className="text-2xs text-ink-3 mb-1">Cumulative P&amp;L</div>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={BORDER} strokeDasharray="0" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fill: INK_3, fontSize: 10 }}
                      axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={24} />
                    <YAxis tick={{ fill: INK_3, fontSize: 10 }} axisLine={false} tickLine={false} width={48}
                      tickFormatter={(v: number) => `$${v}`} />
                    <ReferenceLine y={0} stroke={INK_3} strokeWidth={1} />
                    <Tooltip content={<EquityTooltip />} cursor={{ stroke: INK_3, strokeWidth: 1 }} />
                    <Area type="monotone" dataKey="cumulativePnlDollars" stroke={BULL} strokeWidth={2}
                      fill={BULL} fillOpacity={0.1} dot={false} activeDot={{ r: 4, fill: BULL }} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Per-day bars — diverging encoding: sign is carried by
                  position relative to the zero baseline as well as color,
                  so it's never color-alone */}
              <div>
                <div className="text-2xs text-ink-3 mb-1">Daily P&amp;L</div>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={BORDER} strokeDasharray="0" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fill: INK_3, fontSize: 10 }}
                      axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={24} />
                    <YAxis tick={{ fill: INK_3, fontSize: 10 }} axisLine={false} tickLine={false} width={48}
                      tickFormatter={(v: number) => `$${v}`} />
                    <ReferenceLine y={0} stroke={INK_3} strokeWidth={1} />
                    <Tooltip content={<DailyBarTooltip />} cursor={{ fill: BORDER, opacity: 0.3 }} />
                    <Bar dataKey="pnlDollars" radius={[3, 3, 3, 3]} maxBarSize={24} isAnimationActive={false}>
                      {series.map((d, i) => (
                        <Cell key={i} fill={d.pnlDollars >= 0 ? BULL : BEAR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
